import { useAuthStore } from '@/stores';
import axios from 'axios';
import router from '@/router';

export const fetchWrapper = {
  get: request('GET'),
  post: request('POST'),
  put: request('PUT'),
  delete: request('DELETE'),
  patch: request('PATCH'),
  uploadFile: uploadFile(),
  postFormData: requestFormData(),
  postBlob: postBlob(),
  getBlob: getBlob(),
  authHeader,
};

function request(method) {
  return async (url, body) => {
    const requestOptions = {
      method,
      headers: authHeader(url),
    };

    if (body) {
      requestOptions.headers['Content-Type'] = 'application/json';
      requestOptions.data = body;
    }
    try {
      const response = await axios(url, requestOptions);

      return handleResponse(response);
    } catch (error) {
      console.log('request', error);
      return handleResponse(error.response);
    }
  };
}

function requestFormData() {
  return async (url, body, data, cancelTokenSource) => {
    let response = {};

    try {
      // const response = await axios(url, requestOptions)

      await axios
        .request({
          method: 'post',
          headers: authHeader(url),
          url: url,
          data: body,
          onUploadProgress: async (p) => {
            let progressEvent = p.event;
            if (progressEvent.lengthComputable) {
              const percentComplete =
                (progressEvent.loaded / progressEvent.total) * 100;

              data.progress.value = Math.round(percentComplete);
            }
          },
          cancelToken: cancelTokenSource.token,
        })
        .then((res) => {
          data.progress.value = 100;
          data.uploaded.value = true;
          response = res;
        });

      return handleResponse(response);
    } catch (error) {
      if (error.code == 'ERR_CANCELED') {
        // cancel by user
        response.message = 'cancel';

        return response;
      }

      return handleResponse(error.response);
    }
  };
}

function postBlob() {
  return async (url, body) => {
    const requestOptions = {
      method: 'POST',
      headers: authHeader(url),
      responseType: 'blob', // keep blob for success files
      data: body,
    };

    try {
      const response = await axios(url, requestOptions);
      return handleResponse(response);
    } catch (error) {
      if (error.response && error.response.data instanceof Blob) {
        try {
          // Convert blob to text
          const text = await error.response.data.text();
          return { status: 'error', message: text };
        } catch (e) {
          console.error('Error parsing blob:', e);
        }
      }
    }
  };
}

function getBlob() {
  return async (url) => {
    const requestOptions = {
      method: 'GET',
      headers: authHeader(url),
      responseType: 'blob',
    };

    try {
      const response = await axios(url, requestOptions);
      return handleResponse(response);
    } catch (error) {
      if (error.response && error.response.data instanceof Blob) {
        try {
          // Convert blob to text
          const text = await error.response.data.text();
          return { status: 'error', message: text };
        } catch (e) {
          console.error('Error parsing blob:', e);
        }
      }
    }
  };
}

function uploadFile() {
  return async (url, data) => {
    const requestOptions = {
      method: 'POST',
      headers: authHeader(url),
      data: data,
    };

    try {
      requestOptions.headers['Content-Type'] = 'multipart/form-data';
      const response = await axios(url, requestOptions);
      return handleResponse(response);
    } catch (error) {
      return handleResponse(error.response);
    }
  };
}

function authHeader(url) {
  // return auth header with jwt if user is logged in and request is to the api url
  const user = JSON.parse(
    localStorage.getItem(`${process.env.VUE_APP_NAME}_user`)
  );
  const isLoggedIn = !!user?.token;
  const isApiUrl = url.startsWith(process.env.VUE_APP_PROXY_SERVER);
  if (isLoggedIn && isApiUrl) {
    return { Authorization: `Bearer ${user.token}` };
  } else {
    return {};
  }
}

async function handleResponse(response) {
  const data = response?.data ? response.data : null;

  // check for error response
  if (response.status < 200 || response.status >= 300) {
    const { user, logout } = useAuthStore();

    if ([401].includes(response.status) && user) {
      // auto logout if 401 Unauthorized or 403 Forbidden response returned from api
      logout();
    }

    if (response.status === 423) {
      const current = router.currentRoute.value;
      if (current?.path !== '/activate-license') {
        router.replace({ path: '/activate-license' });
      }

      // Provide a friendly error to callers (optional customize)
      const msg =
        data?.message || data?.errorMessage || 'License is not activated.';
      return Promise.reject(new Error(resolveErrorMessage(msg)));
    }

    // Handle specific error for 429 Conflict
    if (response.status === 429) {
      return Promise.reject(new Error('Conflict: Duplicate page name'));
    } else if (response.status === 409) {
      return Promise.reject(new Error(resolveErrorMessage(response.data)));
    }

    // get error message from body or default to response status
    const error =
      data?.message || data?.errorMessage || data || response.status;

    return Promise.reject(new Error(resolveErrorMessage(error)));
  }

  return data;
}

function resolveErrorMessage(value, fallback = 'Request failed') {
  if (value == null || value === '') return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (typeof value.message === 'string') return value.message;
    if (typeof value.errorMessage === 'string') return value.errorMessage;
    if (typeof value.Message === 'string') return value.Message;
    if (typeof value.title === 'string') return value.title;
    if (typeof value.error === 'string') return value.error;

    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }

  return String(value);
}
